import cn from "classnames";
import { Button } from "components/Button/Button";
import { useEffect, useState } from "react";
import { disableCopyPasteHandlers, disableGrammarlyExtension } from "utils/helper";
import { storage } from "services/storage";
import { API_SAVE_FAILURE_EVENT, ORGANIZATIONS } from "constant";
import { Editor } from "@tinymce/tinymce-react";
import { useIsMobileDevice } from "utils/hooks";
import { useLanguageContext } from "store/LanguageProvider";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle } from "react-icons/fa";


const getPlainTextValue = (value) => {
  if (!value) return "";
  const parser = new DOMParser();
  return parser.parseFromString(value, "text/html").body.textContent ?? "";
}

export const FreeText = ({ onSubmit, time, questionId }) => {

  const organization = storage.get.org();
  const wizrFlag = organization === ORGANIZATIONS.WIZR;

  const { language } = useLanguageContext();
  const { t } = useTranslation()

  const [saveEditorTimer, setSaveEditorTimer] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [minLengthError, setMinLengthError] = useState(false);
  const [showShortResponseModal, setShowShortResponseModal] = useState(false);
  const [loading, setLoading] = useState(false)

  const isMobile = useIsMobileDevice()
  const trimmedInputValue = inputValue.trim();
  const trimmedPlainTextValue = getPlainTextValue(inputValue).trim();
  const shouldConfirmShortResponse = trimmedPlainTextValue.length > 0 && trimmedPlainTextValue.length < 300;

  const handleSaveFailure = () => {
    setLoading(false)
  }

  const submitHandler = (forceSubmit = false) => {
    const checkAnswerMinLength = trimmedInputValue.length >= 10;
    if (!checkAnswerMinLength) {
      setMinLengthError(true);
      if (!forceSubmit) return;
    } else setMinLengthError(false);
    if (!forceSubmit && shouldConfirmShortResponse) {
      setShowShortResponseModal(true);
      return;
    }
    setLoading(true)
    onSubmit({ answerText: trimmedInputValue }, true, false, true);
  };

  useEffect(() => {
    if (time !== null && time === 0) {
      submitHandler(true);
    }
  }, [time]);

  // Check FT_Cache for questionid and saved text for editor
  useEffect(() => {
    const freeTextAnswerCache = storage.get.freeTextCache() ?? {};
    if (freeTextAnswerCache[questionId]) setInputValue(freeTextAnswerCache[questionId])

    window.addEventListener(API_SAVE_FAILURE_EVENT, handleSaveFailure)
    return () => window.removeEventListener(API_SAVE_FAILURE_EVENT, handleSaveFailure)
  }, []);

  const handleEditorLocalStorageSave = () => {
    let tempFreeTextAnswerCache = storage.get.freeTextCache() ?? {};
    tempFreeTextAnswerCache[questionId] = inputValue;
    storage.set.freeTextCache(tempFreeTextAnswerCache)
  }

  // Timer for keeping track of editor changes
  useEffect(() => {
    let editorSaveInterval = setInterval(() => {
      setSaveEditorTimer(prev => prev + 1)
    }, 1000);

    return () => clearInterval(editorSaveInterval)
  }, []);

  // Saving content in LS cache every 20 seconds
  useEffect(() => {
    if (saveEditorTimer % 20 === 0) {
      handleEditorLocalStorageSave();
    }
  }, [saveEditorTimer])

  return (
    <div className={`flex flex-1 w-full flex-col items-center justify-between ${isMobile ? "pb-[52px]" : ""}`}>
      <div className={`w-full flex ${isMobile ? "justify-center fixed bottom-0 left-0 bg-[#fff] pt-1" : "justify-end sticky top-0 bg-[#fff]"} z-[3] p-[2px] mb-[2px]`}>
        <Button
          disabled={!inputValue || loading}
          onClick={(e) => submitHandler(false)}
          className="!bg-cocoa-brown text-white"
        >
          Submit
        </Button>
      </div>
      <Editor
        tinymceScriptSrc={'/tinymce/tinymce.min.js'}
        id={"free-text-input"}
        value={inputValue}
        onEditorChange={(content, editor) => {
          disableGrammarlyExtension();
          if (minLengthError) setMinLengthError(false);
          setInputValue(content);
        }}
        init={{
          min_height: 500,
          max_height: 500,
          width: '100%',
          force_br_newlines: true,
          menubar: false,
          statusbar: false,
          content_css: '/tinyMceContent.css',
          plugins: 'link image code autoresize lists table',
          toolbar:
            'formatselect | bold italic underline | blocks | bullist numlist outdent indent',
          mobile: {
            toolbar_mode: "wrap",
          },
          language: language
        }}
        {...disableCopyPasteHandlers}
      />
      {
        minLengthError &&
        <p className="text-xl text-center text-red-500">{t("free_text.more_chars")}</p>
      }
      {
        showShortResponseModal &&
        <div
          className="fixed inset-0 z-[50] flex items-center justify-center bg-black/[0.35] px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-[440px] rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-4 text-center">
              <div>
                <p className="mb-1 inline-flex items-center justify-center gap-2 text-lg font-medium text-cocoa-brown">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3D6] text-sm text-[#C47A00]">
                    <FaExclamationTriangle />
                  </span>
                  {t("free_text.short_response_title")}
                </p>
                <p className="text-sm leading-5 text-[#5E5046]">
                  {t(
                    "free_text.short_response_warning",
                    "Your response seems too short. Please add a little more detail to address all parts of the question clearly and professionally."
                  )}
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-center">
              <button
                className="min-w-[160px] rounded-full border border-stark-white px-6 py-3 text-sm font-medium text-cocoa-brown"
                onClick={() => setShowShortResponseModal(false)}
              >
                {t("free_text.edit_response")}
              </button>
              <button
                className="min-w-[160px] rounded-full bg-cocoa-brown px-6 py-3 text-sm font-medium text-white"
                onClick={() => {
                  setShowShortResponseModal(false);
                  submitHandler(true);
                }}
              >
                {t("free_text.continue_anyway")}
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  );
};
